import {
    CollisionDescriptor,
    CollisionDetection,
    DroppableContainer,
} from '@givebutter/primitives/draggable';
import { DropTarget } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { DropTargetPayload } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';

interface ExtendedCollisionDescriptor extends CollisionDescriptor {
    data: {
        droppableContainer: DroppableContainer;
        value: number;
    };
}

const getDropTargetPriority = (type: string | undefined): number => {
    switch (type) {
        case DropTarget.OCCUPIED_SEAT:
            return 0;
        case DropTarget.EMPTY_SEAT:
            return 1;
        case DropTarget.TICKET:
            return 2;
        case DropTarget.SEATING_CONTAINER:
        case DropTarget.UNSEATED_CONTAINER:
            return 3;
        default:
            return 4;
    }
};

const isTableHeaderId = (id: string | number): boolean => {
    return id.toString().startsWith(`${DropTarget.TABLE_HEADER}-`);
};

const compareTableHeaders = (
    a: ExtendedCollisionDescriptor,
    b: ExtendedCollisionDescriptor,
): number | null => {
    const aIsTableHeader = isTableHeaderId(a.id);
    const bIsTableHeader = isTableHeaderId(b.id);

    if (aIsTableHeader !== bIsTableHeader) {
        return aIsTableHeader ? -1 : 1;
    }
    return null;
};

const getContainerData = (
    id: string | number,
    containers: DroppableContainer[],
): DropTargetPayload => {
    const container = containers.find(c => c.id === id);
    return container?.data?.current as DropTargetPayload;
};

const compareByPriority = (aData: DropTargetPayload, bData: DropTargetPayload): number | null => {
    const aPriority = getDropTargetPriority(aData?.type);
    const bPriority = getDropTargetPriority(bData?.type);

    if (aPriority !== bPriority) {
        return aPriority - bPriority;
    }
    return null;
};

const createCollisionComparator = (
    isDraggingTableHeader: boolean,
    droppableContainers: DroppableContainer[],
) => {
    return (a: ExtendedCollisionDescriptor, b: ExtendedCollisionDescriptor): number => {
        if (isDraggingTableHeader) {
            const tableHeaderResult = compareTableHeaders(a, b);
            if (tableHeaderResult !== null) {
                return tableHeaderResult;
            }
        }

        const aData = getContainerData(a.id, droppableContainers);
        const bData = getContainerData(b.id, droppableContainers);

        const priorityResult = compareByPriority(aData, bData);
        if (priorityResult !== null) {
            return priorityResult;
        }

        return (a.data?.value || 0) - (b.data?.value || 0);
    };
};

export const pointerWithin: CollisionDetection = ({
    active,
    droppableRects,
    droppableContainers,
    pointerCoordinates,
    collisionRect,
}) => {
    // keyboard navigation
    if (!pointerCoordinates && collisionRect) {
        const collisions = droppableContainers
            .map(droppableContainer => {
                const { id } = droppableContainer;
                const rect = droppableRects.get(id);

                if (!rect) return null;

                const intersects =
                    collisionRect.left < rect.right &&
                    collisionRect.right > rect.left &&
                    collisionRect.top < rect.bottom &&
                    collisionRect.bottom > rect.top;

                if (!intersects) return null;

                return {
                    id,
                    data: {
                        droppableContainer,
                        value: 0,
                    },
                };
            })
            .filter((collision): collision is ExtendedCollisionDescriptor => collision !== null);

        return collisions.sort(createCollisionComparator(false, droppableContainers));
    }

    if (!pointerCoordinates) {
        return [];
    }

    // cursor navigation
    const activeId = active?.id;
    const isDraggingTableHeader =
        typeof activeId === 'string' && activeId.includes(`${DropTarget.TABLE_HEADER}-`);

    const collisions = droppableContainers
        .map(droppableContainer => {
            const { id } = droppableContainer;
            const rect = droppableRects.get(id);

            if (!rect || !droppableContainer.data?.current) return null;

            const dropData = droppableContainer.data.current as DropTargetPayload;

            if (isDraggingTableHeader && dropData.type !== DropTarget.TABLE_HEADER) return null;

            const isWithin =
                pointerCoordinates.x >= rect.left &&
                pointerCoordinates.x <= rect.right &&
                pointerCoordinates.y >= rect.top &&
                pointerCoordinates.y <= rect.bottom;

            if (!isWithin) return null;

            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const distance = Math.sqrt(
                Math.pow(pointerCoordinates.x - centerX, 2) +
                    Math.pow(pointerCoordinates.y - centerY, 2),
            );

            return {
                id,
                data: {
                    droppableContainer,
                    value: distance,
                },
            };
        })
        .filter((collision): collision is ExtendedCollisionDescriptor => collision !== null);

    return collisions.sort(createCollisionComparator(isDraggingTableHeader, droppableContainers));
};
